import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getCampaign, updateCampaign, updateCampaignStatus,
  addCampaignRecipients, removeCampaignRecipient,
  generateCampaignContent, getCampaignAnalytics,
  getLeads, convertLead, getLeadSegmentations,
} from '../services/api';
import Modal from '../components/Modal';
import { useDialog } from '../components/DialogProvider';
import AzotteGrid from '../components/AzotteGrid';
import {
  Box, Typography, Button, Paper, Stack, Alert, Tabs, Tab, Chip,
  TextField, MenuItem, Grid, CircularProgress, Checkbox, FormControlLabel,
  DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const STATUS_COLORS = { Draft: '#3b82f6', Active: '#22c55e', Paused: '#9ca3af', Completed: '#8b5cf6', Archived: '#374151' };
const OUTREACH_STATUS_COLORS = { Pending: '#9ca3af', Queued: '#3b82f6', Sent: '#f59e0b', Delivered: '#22c55e', Opened: '#8b5cf6', Replied: '#22c55e', Bounced: '#ef4444', Failed: '#ef4444' };
const TONES = ['Formal', 'Direct', 'Friendly', 'Consultative'];
const PERSONAS = ['CTO', 'CFO', 'ProductManager', 'CEO', 'VP_Engineering', 'VP_Sales', 'Other'];

const TAB_MAP = { 0: 'overview', 1: 'recipients', 2: 'analytics' };
const TAB_INDEX = { overview: 0, recipients: 1, analytics: 2 };

export default function CampaignDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dialog = useDialog();
  const [campaign, setCampaign] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [analytics, setAnalytics] = useState(null);

  // Modals
  const [addRecipientModal, setAddRecipientModal] = useState(false);
  const [recipientForm, setRecipientForm] = useState({});
  const [segmentations, setSegmentations] = useState([]);
  const [previewModal, setPreviewModal] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatingSingle, setGeneratingSingle] = useState(null);
  const [leads, setLeads] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  // Grid pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchCampaign = useCallback(async () => {
    try {
      const { data } = await getCampaign(id);
      setCampaign(data);
    } catch { setError('Failed to load campaign'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchCampaign(); }, [fetchCampaign]);

  useEffect(() => {
    if (activeTab === 'analytics' && campaign) {
      getCampaignAnalytics(id).then(r => setAnalytics(r.data)).catch(() => {});
    }
  }, [activeTab, id, campaign]);

  const handleStatusChange = async (newStatus) => {
    try {
      await updateCampaignStatus(id, { status: newStatus });
      fetchCampaign();
    } catch (err) { setError(err.response?.data?.message || 'Failed to update status'); }
  };

  const openAddRecipients = async () => {
    setRecipientForm({ segmentationId: '', leadIds: [], channel: 'Email', defaultPersona: '' });
    try {
      const { data } = await getLeads();
      setLeads(data);
    } catch { }
    try {
      const { data } = await getLeadSegmentations();
      setSegmentations(Array.isArray(data) ? data : (data?.items || []));
    } catch { }
    setAddRecipientModal(true);
  };

  const handleAddRecipients = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        segmentationId: recipientForm.segmentationId ? parseInt(recipientForm.segmentationId, 10) : null,
        leadIds: recipientForm.leadIds.length > 0 ? recipientForm.leadIds : null,
        channel: recipientForm.channel,
        defaultPersona: recipientForm.defaultPersona || null,
      };
      await addCampaignRecipients(id, payload);
      setAddRecipientModal(false);
      fetchCampaign();
    } catch (err) { setError(err.response?.data?.message || 'Failed to add recipients'); }
  };

  const handleRemoveRecipient = async (recipientId) => {
    try {
      await removeCampaignRecipient(id, recipientId);
      fetchCampaign();
    } catch (err) { setError(err.response?.data?.message || 'Cannot remove'); }
  };

  const handleBulkGenerate = async () => {
    setGenerating(true);
    setError('');
    try {
      const { data } = await generateCampaignContent(id, {});
      setError(`Generated: ${data.generatedCount}, Failed: ${data.failedCount}`);
      fetchCampaign();
    } catch (err) { setError(err.response?.data?.message || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const handleSingleGenerate = async (recipientId) => {
    setGeneratingSingle(recipientId);
    try {
      await generateCampaignContent(id, { recipientIds: [recipientId] });
      fetchCampaign();
    } catch (err) { setError(err.response?.data?.message || 'Generation failed'); }
    finally { setGeneratingSingle(null); }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    try {
      await updateCampaign(id, {
        ...editForm,
        interactionTemplateId: editForm.interactionTemplateId ? parseInt(editForm.interactionTemplateId) : null,
        targetIcpProfileId: editForm.targetIcpProfileId ? parseInt(editForm.targetIcpProfileId) : null,
        targetSegment: editForm.targetSegment || null,
        defaultPersona: editForm.defaultPersona || null,
        startDate: editForm.startDate || null,
        endDate: editForm.endDate || null,
      });
      setEditing(false);
      fetchCampaign();
    } catch (err) { setError(err.response?.data?.message || 'Failed to update'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '-';

  if (loading) return <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress /></Box>;
  if (!campaign) return <Box sx={{ p: 5, textAlign: 'center' }}><Typography color="text.secondary">Campaign not found</Typography></Box>;

  const statusActions = {
    Draft: [{ label: 'Activate', status: 'Active', color: '#22c55e' }],
    Active: [{ label: 'Pause', status: 'Paused', color: '#9ca3af' }, { label: 'Complete', status: 'Completed', color: '#8b5cf6' }],
    Paused: [{ label: 'Resume', status: 'Active', color: '#22c55e' }],
  };

  const recipientColumns = [
    { key: 'leadName', header: 'Lead', render: (val) => <Typography sx={{ fontWeight: 500 }}>{val}</Typography> },
    {
      key: 'contactName', header: 'Contact', render: (val, row) => (
        <Box>
          <Typography variant="body2">{val || '-'}</Typography>
          {row.contactJobTitle && <Typography variant="caption" color="text.secondary">{row.contactJobTitle}</Typography>}
        </Box>
      ),
    },
    { key: 'persona', header: 'Persona', render: (val) => val || '-' },
    { key: 'tone', header: 'Tone', render: (val) => val || campaign.defaultTone },
    { key: 'channel', header: 'Channel', render: (val) => <Chip label={val} size="small" variant="outlined" /> },
    {
      key: 'personalizedSubject', header: 'Content', render: (val, row) =>
        val ? (
          <Button size="small" variant="outlined" onClick={() => setPreviewModal(row)}>Preview</Button>
        ) : (
          <Typography variant="caption" color="text.secondary">Not generated</Typography>
        ),
    },
    {
      key: 'status', header: 'Status', render: (val) => (
        <Typography sx={{ color: OUTREACH_STATUS_COLORS[val] || '#718096', fontWeight: 600, fontSize: '0.85rem' }}>{val}</Typography>
      ),
    },
    { key: 'sentAt', header: 'Sent', render: (val) => <Typography variant="caption">{formatDate(val)}</Typography> },
    { key: 'openedAt', header: 'Opened', render: (val) => <Typography variant="caption">{formatDate(val)}</Typography> },
    {
      key: 'actions', header: 'Actions', render: (_, row) => (
        <Stack direction="row" spacing={0.5}>
          {campaign.useAiPersonalization && row.status === 'Pending' && (
            <Button size="small" variant="contained" disabled={generatingSingle === row.id} onClick={() => handleSingleGenerate(row.id)}>
              {generatingSingle === row.id ? '...' : 'Generate'}
            </Button>
          )}
          <Button size="small" variant="outlined" onClick={async () => {
            try {
              const { data } = await convertLead(row.leadId, {});
              if (data.success) {
                await dialog.alert({ title: 'Lead converted', message: `Converted ${row.leadName} to customer: ${data.customerName}` });
                navigate(`/customers/${data.customerId}`);
              } else { setError(data.errorMessage || 'Cannot convert'); }
            } catch (err) { setError(err.response?.data?.message || 'Conversion failed'); }
          }}>Convert</Button>
          {row.status === 'Pending' && (
            <Button size="small" variant="outlined" color="error" onClick={() => handleRemoveRecipient(row.id)}>Del</Button>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/engage')} sx={{ mb: 2 }}>
        Back to Engage
      </Button>

      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5">{campaign.name}</Typography>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 0.5 }}>
            <Typography sx={{ color: STATUS_COLORS[campaign.status], fontWeight: 700 }}>{campaign.status}</Typography>
            <Chip label={campaign.type} size="small" variant="outlined" />
            {campaign.defaultTone && <Typography variant="caption" color="text.secondary">Tone: {campaign.defaultTone}</Typography>}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1}>
          {(statusActions[campaign.status] || []).map(a => (
            <Button key={a.status} size="small" variant="outlined" sx={{ borderColor: a.color, color: a.color }} onClick={() => handleStatusChange(a.status)}>{a.label}</Button>
          ))}
          <Button size="small" variant="outlined" onClick={() => handleStatusChange('Archived')}>Archive</Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Tabs value={TAB_INDEX[activeTab]} onChange={(_, v) => setActiveTab(TAB_MAP[v])} sx={{ mb: 2 }}>
        <Tab label="Overview" />
        <Tab label={`Recipients (${campaign.recipients.length})`} />
        <Tab label="Analytics" />
      </Tabs>

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
        <Paper sx={{ p: 3, mb: 2.5 }}>
          <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="h6">Campaign Info</Typography>
            {(campaign.status === 'Draft' || campaign.status === 'Paused') && !editing && (
              <Button size="small" variant="outlined" onClick={() => { setEditForm(campaign); setEditing(true); }}>Edit</Button>
            )}
          </Stack>
          {editing ? (
            <form onSubmit={handleSaveEdit}>
              <Box sx={{ mb: 2 }}>
                <TextField label="Name" required size="small" fullWidth value={editForm.name || ''} onChange={e => setEditForm({ ...editForm, name: e.target.value })} />
              </Box>
              <Box sx={{ mb: 2 }}>
                <TextField label="Description" size="small" fullWidth multiline rows={2} value={editForm.description || ''} onChange={e => setEditForm({ ...editForm, description: e.target.value })} />
              </Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField select label="Default Tone" size="small" fullWidth value={editForm.defaultTone || 'Direct'} onChange={e => setEditForm({ ...editForm, defaultTone: e.target.value })}>
                    {TONES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField select label="Default Persona" size="small" fullWidth value={editForm.defaultPersona || ''} onChange={e => setEditForm({ ...editForm, defaultPersona: e.target.value })}>
                    <MenuItem value="">— None —</MenuItem>
                    {PERSONAS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                  </TextField>
                </Grid>
              </Grid>
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={<Checkbox checked={editForm.useAiPersonalization || false} onChange={e => setEditForm({ ...editForm, useAiPersonalization: e.target.checked })} />}
                  label="Use AI Personalization"
                />
              </Box>
              <DialogActions sx={{ px: 0 }}>
                <Button variant="outlined" onClick={() => setEditing(false)}>Cancel</Button>
                <Button type="submit" variant="contained">Save</Button>
              </DialogActions>
            </form>
          ) : (
            <Grid container spacing={2}>
              {[
                { label: 'Description', value: campaign.description || '-' },
                { label: 'Type', value: campaign.type },
                { label: 'Target Segment', value: campaign.targetSegment || 'All' },
                { label: 'ICP Profile', value: campaign.targetIcpProfileName || '-' },
                { label: 'Interaction Template', value: campaign.interactionTemplateName || '-' },
                { label: 'AI Personalization', value: campaign.useAiPersonalization ? 'Enabled' : 'Disabled' },
                { label: 'Default Tone', value: campaign.defaultTone },
                { label: 'Default Persona', value: campaign.defaultPersona || '-' },
                { label: 'Start Date', value: formatDate(campaign.startDate) },
                { label: 'End Date', value: formatDate(campaign.endDate) },
              ].map((item, i) => (
                <Grid key={i} size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                  <Typography variant="body2">{item.value}</Typography>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      )}

      {/* RECIPIENTS TAB */}
      {activeTab === 'recipients' && (
        <Paper sx={{ p: 3, mb: 2.5 }}>
          <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="h6">Recipients</Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="contained" onClick={openAddRecipients}>+ Add Recipients</Button>
              {campaign.useAiPersonalization && (
                <Button size="small" variant="outlined" disabled={generating} onClick={handleBulkGenerate}>
                  {generating ? 'Generating...' : 'Bulk Generate'}
                </Button>
              )}
            </Stack>
          </Stack>

          {campaign.recipients.length === 0 ? (
            <Box sx={{ p: 5, textAlign: 'center' }}>
              <Typography color="text.secondary" sx={{ mb: 2 }}>No recipients yet</Typography>
              <Button variant="contained" onClick={openAddRecipients}>Add Recipients</Button>
            </Box>
          ) : (
            <AzotteGrid
              columns={recipientColumns}
              rows={campaign.recipients}
              totalCount={campaign.recipients.length}
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              handleRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
              loading={false}
              actionColumnKey="actions"
            />
          )}
        </Paper>
      )}

      {/* ANALYTICS TAB */}
      {activeTab === 'analytics' && (
        <Box>
          {analytics ? (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { label: 'Total', value: analytics.totalRecipients },
                  { label: 'Sent', value: analytics.sent },
                  { label: 'Delivered', value: analytics.delivered },
                  { label: 'Opened', value: analytics.opened },
                ].map((s, i) => (
                  <Grid key={i} size={{ xs: 6, sm: 3 }}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'primary.main' }}>{s.value}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>{s.label}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { label: 'Open Rate', value: `${analytics.openRate}%`, color: analytics.openRate >= 20 ? '#22c55e' : '#ef4444' },
                  { label: 'Reply Rate', value: `${analytics.replyRate}%`, color: analytics.replyRate >= 5 ? '#22c55e' : '#ef4444' },
                  { label: 'Bounce Rate', value: `${analytics.bounceRate}%`, color: analytics.bounceRate > 5 ? '#ef4444' : '#22c55e' },
                ].map((s, i) => (
                  <Grid key={i} size={{ xs: 12, sm: 4 }}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: s.color }}>{s.value}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>{s.label}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Paper sx={{ p: 3, mb: 2.5 }}>
                <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Typography variant="h6">Status Breakdown</Typography>
                </Stack>
                <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
                  {[
                    { label: 'Replied', count: analytics.replied, color: '#22c55e' },
                    { label: 'Opened', count: analytics.opened - analytics.replied, color: '#8b5cf6' },
                    { label: 'Delivered', count: analytics.delivered - analytics.opened, color: '#3b82f6' },
                    { label: 'Sent', count: analytics.sent - analytics.delivered, color: '#f59e0b' },
                    { label: 'Bounced', count: analytics.bounced, color: '#ef4444' },
                    { label: 'Failed', count: analytics.failed, color: '#991b1b' },
                  ].filter(s => s.count > 0).map((s, i) => (
                    <Stack key={i} direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: s.color }} />
                      <Typography variant="body2">{s.label}: {s.count}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </>
          ) : <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress /></Box>}
        </Box>
      )}

      {/* ADD RECIPIENTS MODAL */}
      {addRecipientModal && (
        <Modal title="Add Recipients" onClose={() => setAddRecipientModal(false)}>
          <form onSubmit={handleAddRecipients}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select label="Filter by Segmentation" size="small" fullWidth value={recipientForm.segmentationId || ''} onChange={e => setRecipientForm({ ...recipientForm, segmentationId: e.target.value })} SelectProps={{ displayEmpty: true }}>
                  <MenuItem value="">— All —</MenuItem>
                  {segmentations.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select label="Channel" size="small" fullWidth value={recipientForm.channel} onChange={e => setRecipientForm({ ...recipientForm, channel: e.target.value })}>
                  {['Email', 'LinkedIn', 'Phone'].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
              </Grid>
            </Grid>
            <Box sx={{ mb: 2 }}>
              <TextField select label="Default Persona" size="small" fullWidth value={recipientForm.defaultPersona || ''} onChange={e => setRecipientForm({ ...recipientForm, defaultPersona: e.target.value })}>
                <MenuItem value="">— Use Campaign Default —</MenuItem>
                {PERSONAS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>Or Select Specific Leads</Typography>
              <Box sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1 }}>
                {leads.map(p => (
                  <FormControlLabel
                    key={p.id}
                    sx={{ display: 'flex', py: 0.25, '& .MuiTypography-root': { fontSize: '0.875rem' } }}
                    control={
                      <Checkbox size="small" checked={recipientForm.leadIds.includes(p.id)}
                        onChange={(e) => {
                          const ids = e.target.checked
                            ? [...recipientForm.leadIds, p.id]
                            : recipientForm.leadIds.filter(x => x !== p.id);
                          setRecipientForm({ ...recipientForm, leadIds: ids });
                        }} />
                    }
                    label={p.companyName}
                  />
                ))}
                {leads.length === 0 && <Typography variant="body2" color="text.secondary">No leads available</Typography>}
              </Box>
            </Box>
            <DialogActions sx={{ px: 0 }}>
              <Button variant="outlined" onClick={() => setAddRecipientModal(false)}>Cancel</Button>
              <Button type="submit" variant="contained">Add Recipients</Button>
            </DialogActions>
          </form>
        </Modal>
      )}

      {/* PREVIEW MODAL */}
      {previewModal && (
        <Modal title="Email Preview" onClose={() => setPreviewModal(null)}>
          <Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Subject</Typography>
              <Typography sx={{ fontWeight: 600, py: 1 }}>{previewModal.personalizedSubject}</Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Body</Typography>
              <Box sx={{ bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, fontSize: '0.875rem', lineHeight: 1.7 }}
                dangerouslySetInnerHTML={{ __html: previewModal.personalizedBody }} />
            </Box>
            <Stack direction="row" spacing={1}>
              <Typography variant="caption" color="text.secondary">Provider: {previewModal.aiProviderUsed || '-'}</Typography>
              <Typography variant="caption" color="text.secondary">Persona: {previewModal.persona || '-'}</Typography>
              <Typography variant="caption" color="text.secondary">Tone: {previewModal.tone || campaign.defaultTone}</Typography>
            </Stack>
          </Box>
        </Modal>
      )}
    </Box>
  );
}
